import classNames from "classnames";
import React, {
    HTMLAttributes,
    ReactNode,
    useMemo,
} from "react";
import { RiAddFill } from "@remixicon/react";
import useTableData, { Events } from "./base";
import Styles from "./Table.module.scss";
import Text from "../text/Text";
import Button from "../button/Button";

export interface TableEmptyDefaultProps extends HTMLAttributes<HTMLDivElement>{
    icon?: ReactNode;
    title: string;
    text: ReactNode;
    buttonText?: string;
    onClick?: () => void;
}

const TableEmptyDefault = React.forwardRef((
    args: TableEmptyDefaultProps,
    ref: React.ForwardedRef<HTMLDivElement>,
): ReactNode => {
    const {
        className,
        icon,
        title,
        text,
        buttonText,
        onClick,
        ...rest
    } = args;

    return (
        <div className={classNames(Styles["table-no-results"], className)} ref={ref} {...rest}>

            <div className={Styles["table-no-results-container"]}>
                {icon && (
                    <div className={Styles["table-no-results-icon"]}>{icon}</div>
                )}
                <div className={Styles["table-no-results-text"]}>
                    <Text textVariant="subtitle-2" colorVariant="text-title" as="h2">{title}</Text>
                    <Text textVariant="body-1-regular" colorVariant="body-secondary">{text}</Text>
                </div>
                {onClick && (
                    <Button size="large" prefixIcon={<RiAddFill />} onClick={onClick}>
                        {buttonText || "Create Item"}
                    </Button>
                )}
            </div>
        </div>
    );
});

export type TableEmptyProps = HTMLAttributes<HTMLDivElement>

const TableEmpty = React.forwardRef((
    args: TableEmptyProps,
    ref: React.ForwardedRef<HTMLDivElement>,
): ReactNode => {
    const data = useTableData();

    const generation = data.events.useEvent(Events.UPDATED_DATA);

    const empty = useMemo(
        () => (args.children ? <div ref={ref} {...args} /> : undefined),
        [args, ref, args.children, generation, data],
    );

    data.useEmptyElement(empty);

    return null;
});

TableEmpty.defaultProps = {

};

const Table = TableEmpty as typeof TableEmpty & {
    Default: typeof TableEmptyDefault
};
Table.Default = TableEmptyDefault;

export default Table;
